import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag } from 'lucide-react';
import { motion } from 'framer-motion';
import { inr, discountPct, cx } from '@/lib/format';
import { useStore } from '@/context/StoreContext';
import Stars from '@/components/common/Stars';

const Badge = ({ children, variant }) => {
  const map = {
    new: 'bg-[hsl(var(--gaze-ivory))] text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/20',
    sale: 'bg-white text-foreground border border-[hsl(var(--gaze-gold))]/60',
    best: 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]',
    limited: 'bg-transparent text-[hsl(var(--gaze-gold))] border border-[hsl(var(--gaze-gold))]/70',
    clearance: 'bg-[hsl(var(--gaze-black))] text-white',
  };
  return (
    <span className={cx('inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold tracking-wide', map[variant])}>
      {children}
    </span>
  );
};

export const ProductCard = ({ product, index = 0 }) => {
  const { toggleWishlist, inWishlist, addToCart } = useStore();
  const wished = inWishlist(product.id);
  const pct = discountPct(product.price, product.mrp);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.4, delay: (index % 4) * 0.05 }}
      className="group"
      data-testid="product-card"
    >
      <div className="relative overflow-hidden rounded-2xl bg-white shadow-[0_1px_0_rgba(17,17,17,0.06)] transition-shadow hover:shadow-[0_18px_50px_rgba(17,17,17,0.10)]">
        <Link to={`/product/${product.slug}`} className="block" aria-label={product.name}>
          <div className="relative aspect-[4/5] overflow-hidden bg-[hsl(var(--gaze-ivory))]">
            <img
              src={(product.images || [])[0]}
              alt={product.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
            />
            <div className="absolute left-3 top-3 flex flex-col gap-1.5">
              {product.is_clearance ? <Badge variant="clearance">Clearance</Badge> : product.is_new ? <Badge variant="new">New</Badge> : null}
              {product.is_limited && <Badge variant="limited">Limited</Badge>}
              {!product.is_new && !product.is_clearance && pct > 0 && <Badge variant="sale">-{pct}%</Badge>}
            </div>
            {!product.in_stock && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-foreground shadow">Out of Stock</span>
              </div>
            )}
          </div>
        </Link>

        <button
          type="button"
          onClick={() => toggleWishlist(product)}
          data-testid="product-card-wishlist-button"
          aria-label="Toggle wishlist"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 backdrop-blur transition hover:scale-105"
        >
          <Heart size={16} className={wished ? 'fill-[hsl(var(--destructive))] text-[hsl(var(--destructive))]' : 'text-foreground'} />
        </button>

        {product.in_stock && (
          <button
            type="button"
            onClick={() => addToCart(product, 1)}
            data-testid="product-card-quick-add-button"
            className="absolute bottom-[92px] left-1/2 flex -translate-x-1/2 translate-y-3 items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-xs font-semibold text-[hsl(var(--primary-foreground))] opacity-0 shadow-lg transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
          >
            <ShoppingBag size={14} /> Quick Add
          </button>
        )}

        <div className="p-3 sm:p-4">
          <Link to={`/product/${product.slug}`}>
            <p className="eyebrow mb-1 !tracking-[0.15em]">{product.category_name}</p>
            <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug" style={{ fontFamily: 'var(--font-sans)' }}>
              {product.name}
            </h3>
          </Link>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-base font-bold tabular-nums" data-testid="product-card-price">{inr(product.price)}</span>
            {product.mrp > product.price && (
              <span className="text-xs text-muted-foreground line-through tabular-nums">{inr(product.mrp)}</span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <Stars value={product.rating} />
            <span className="text-xs text-muted-foreground">({product.num_reviews})</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
